import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Database, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface CoverageStats {
  totalFamilies: number;
  wellnessAssessments: {
    count: number;
    percentage: number;
  };
  mealSuggestions: {
    count: number;
    percentage: number;
  };
  mealAnalysis: {
    count: number;
    percentage: number;
  };
}

interface FamilyGap {
  family_id: string;
  missing_types: string[];
  last_wellness?: string;
  last_suggestions?: string;
  last_meal_analysis?: string;
}

export const DataCoverageMonitor: React.FC = () => {
  const [coverageStats, setCoverageStats] = useState<CoverageStats | null>(null);
  const [familyGaps, setFamilyGaps] = useState<FamilyGap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchCoverageData = async () => {
    setIsLoading(true);
    try {
      // Call the database function to get accurate statistics with proper date filtering
      const { data, error } = await supabase.rpc('get_data_coverage_stats');

      if (error) {
        throw new Error(`Failed to fetch data coverage statistics: ${error.message}`);
      }

      // Transform the database response to match our component state
      const result = data as any; // Type assertion for the database function response
      setCoverageStats({
        totalFamilies: result.totalFamilies,
        wellnessAssessments: {
          count: result.wellness.count,
          percentage: result.wellness.percentage
        },
        mealSuggestions: {
          count: result.suggestions.count,
          percentage: result.suggestions.percentage
        },
        mealAnalysis: {
          count: result.analysis.count,
          percentage: result.analysis.percentage
        }
      });

      // For now, clear family gaps since we're focusing on the main statistics
      // The database function provides accurate counts, but detailed gap analysis 
      // would require additional queries if needed in the future
      setFamilyGaps([]);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch coverage data:', err);
      toast.error('Failed to fetch data coverage information');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverageData();
  }, []);

  const getCoveragePercentage = (missing: number, total: number) => {
    if (total === 0) return 100;
    return Math.round(((total - missing) / total) * 100);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            <CardTitle>Data Coverage Monitor</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Last updated: {formatDistanceToNow(lastRefresh, { addSuffix: true })}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchCoverageData}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin mr-2" />
            <span>Loading data coverage...</span>
          </div>
        ) : coverageStats ? (
          <div className="space-y-6">
            {/* Data Coverage Summary Table */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Data Coverage Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-muted-foreground">Job Type</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Families with Recent Data</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Families Missing Data</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Coverage %</th>
                    </tr>
                  </thead>
                  <tbody>
                     <tr className="border-b hover:bg-muted/20">
                       <td className="p-3 font-medium">Weekly Wellness Analysis</td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-green-100 text-green-800">
                           {coverageStats.wellnessAssessments.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-red-100 text-red-800">
                           {coverageStats.totalFamilies - coverageStats.wellnessAssessments.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="outline">
                           {coverageStats.wellnessAssessments.percentage}%
                         </Badge>
                       </td>
                     </tr>
                     <tr className="border-b hover:bg-muted/20">
                       <td className="p-3 font-medium">AI Dinner Suggestions</td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-green-100 text-green-800">
                           {coverageStats.mealSuggestions.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-red-100 text-red-800">
                           {coverageStats.totalFamilies - coverageStats.mealSuggestions.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="outline">
                           {coverageStats.mealSuggestions.percentage}%
                         </Badge>
                       </td>
                     </tr>
                     <tr className="border-b hover:bg-muted/20">
                       <td className="p-3 font-medium">Meal Analysis</td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-green-100 text-green-800">
                           {coverageStats.mealAnalysis.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="secondary" className="bg-red-100 text-red-800">
                           {coverageStats.totalFamilies - coverageStats.mealAnalysis.count}
                         </Badge>
                       </td>
                       <td className="p-3 text-right">
                         <Badge variant="outline">
                           {coverageStats.mealAnalysis.percentage}%
                         </Badge>
                       </td>
                     </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">{coverageStats.totalFamilies}</div>
                <div className="text-sm text-muted-foreground">Total Families</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-600">
                  {Math.min(coverageStats.wellnessAssessments.count, coverageStats.mealSuggestions.count, coverageStats.mealAnalysis.count)}
                </div>
                <div className="text-sm text-green-700">Complete Coverage</div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-600">{familyGaps.length}</div>
                <div className="text-sm text-yellow-700">Missing Data</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round(((coverageStats.wellnessAssessments.percentage + coverageStats.mealSuggestions.percentage + coverageStats.mealAnalysis.percentage) / 3))}%
                </div>
                <div className="text-sm text-blue-700">Avg Coverage Rate</div>
              </div>
            </div>

            {/* Coverage by Data Type */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Coverage by Data Type</h3>
              <div className="space-y-2">
                 <div className="flex items-center justify-between p-3 bg-muted/30 rounded">
                   <div className="flex items-center gap-2">
                     <CheckCircle className="h-4 w-4 text-green-600" />
                     <span>Wellness Assessments (last 2 weeks)</span>
                   </div>
                   <div className="flex items-center gap-2">
                     <Badge variant="secondary">
                       {coverageStats.wellnessAssessments.percentage}%
                     </Badge>
                     <span className="text-sm text-muted-foreground">
                       {coverageStats.wellnessAssessments.count}/{coverageStats.totalFamilies}
                     </span>
                   </div>
                 </div>
                 
                 <div className="flex items-center justify-between p-3 bg-muted/30 rounded">
                   <div className="flex items-center gap-2">
                     <CheckCircle className="h-4 w-4 text-blue-600" />
                     <span>AI Meal Suggestions (last week)</span>
                   </div>
                   <div className="flex items-center gap-2">
                     <Badge variant="secondary">
                       {coverageStats.mealSuggestions.percentage}%
                     </Badge>
                     <span className="text-sm text-muted-foreground">
                       {coverageStats.mealSuggestions.count}/{coverageStats.totalFamilies}
                     </span>
                   </div>
                 </div>
                 
                 <div className="flex items-center justify-between p-3 bg-muted/30 rounded">
                   <div className="flex items-center gap-2">
                     <CheckCircle className="h-4 w-4 text-purple-600" />
                     <span>Meal Analysis Data (last month)</span>
                   </div>
                   <div className="flex items-center gap-2">
                     <Badge variant="secondary">
                       {coverageStats.mealAnalysis.percentage}%
                     </Badge>
                     <span className="text-sm text-muted-foreground">
                       {coverageStats.mealAnalysis.count}/{coverageStats.totalFamilies}
                     </span>
                   </div>
                 </div>
              </div>
            </div>

            {/* Families with Gaps */}
            {familyGaps.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <h3 className="text-lg font-semibold">Families Missing Data (Top 20)</h3>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {familyGaps.map((gap, index) => (
                    <div key={gap.family_id} className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <code className="text-xs font-mono">{gap.family_id.slice(0, 8)}...</code>
                        <div className="flex gap-1">
                          {gap.missing_types.map(type => (
                            <Badge key={type} variant="outline" className="text-xs">
                              Missing {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground grid grid-cols-3 gap-2">
                        <div>
                          Wellness: {gap.last_wellness ? 
                            formatDistanceToNow(new Date(gap.last_wellness), { addSuffix: true }) : 
                            'Never'
                          }
                        </div>
                        <div>
                          Suggestions: {gap.last_suggestions ? 
                            formatDistanceToNow(new Date(gap.last_suggestions), { addSuffix: true }) : 
                            'Never'
                          }
                        </div>
                        <div>
                          Analysis: {gap.last_meal_analysis ? 
                            formatDistanceToNow(new Date(gap.last_meal_analysis), { addSuffix: true }) : 
                            'Never'
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No coverage data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};